import { useEffect, useRef, useState } from "react";
import { getSurveyUrl } from "../utils/surveyUrl";

// ────────────────────────────────────────────────────────────────────────────
// HAEVN — /a — Editorial Quiet Luxury Variant
// Design ref: /app/design_guidelines.json
// ────────────────────────────────────────────────────────────────────────────

const LOGO_DARK =
  "https://customer-assets.emergentagent.com/job_c41ac942-b289-4e5b-a0c4-9845a8b228fb/artifacts/hsq2iw0w_Asset%2010%404x-8.png";
const LOGO_LIGHT =
  "https://customer-assets.emergentagent.com/job_c41ac942-b289-4e5b-a0c4-9845a8b228fb/artifacts/mw1vpqkw_Asset%208%404x-8.png";

const HERO_IMAGE =
  "https://customer-assets.emergentagent.com/job_b7298b82-3b4b-4657-8b99-bf271ef0bf28/artifacts/16y6k194_Rhys_Sterling_a_person_standing_slightly_apart_in_a_casual_so_814af5fb-69bd-4543-bd7b-fec82cd1c4ef_0.png";

const COLLAGE_IMAGE =
  "https://customer-assets.emergentagent.com/job_b7298b82-3b4b-4657-8b99-bf271ef0bf28/artifacts/0l78lnu2_image.png";

const TRUST_AVATARS = [
  "https://customer-assets.emergentagent.com/job_b7298b82-3b4b-4657-8b99-bf271ef0bf28/artifacts/71cpvtoq_Rhys_Sterling_Candid_photo_of_a_happy_lesbian_couple_--ar_34__4ab7917b-b881-438e-8341-92c1f1597aba_0.png",
  "https://customer-assets.emergentagent.com/job_b7298b82-3b4b-4657-8b99-bf271ef0bf28/artifacts/urt64t7k_Rhys_Sterling_candid_photo_of_a_slightly_amused_woman_full_co_68bc7442-8721-4f56-8764-80560d0994fb_0.png",
  "https://customer-assets.emergentagent.com/job_b7298b82-3b4b-4657-8b99-bf271ef0bf28/artifacts/lc654hzv_Rhys_Sterling_httpss.mj.runKcMpilRjZiY_Draw_this_man_but_in_s_643e2fba-5059-4fd0-bfb5-dcc1b4c3ab1f_2.png",
  "https://customer-assets.emergentagent.com/job_b7298b82-3b4b-4657-8b99-bf271ef0bf28/artifacts/noryrv7w_Rhys_Sterling_httpss.mj.run70wNixDUbBQ_Man_casually_sitting_i_c6877ffb-c17b-4c09-8d87-0e415b3c93b7_3.png",
];

// Design tokens — HAEVN brand palette applied to editorial blueprint
const C = {
  bg: "#FAFAF8",
  surface: "#F5F4F1",
  surfaceAlt: "#FFFFFF",
  textPrimary: "#1E2A4A",
  textSecondary: "#4A5468",
  hairline: "#E0DEDA",
  accent: "#E29E0C",
  accentHover: "#C58708",
  onAccent: "#FFFFFF",
  darkBg: "#1A1A1A",
  darkText: "#FAFAF8",
};

const SERIF = "'Cormorant Garamond', ui-serif, Georgia, serif";
const SANS =
  "'Inter', ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif";

// ─── Reveal-on-scroll hook ──────────────────────────────────────────────────
function useReveal(threshold = 0.12) {
  const ref = useRef(null);
  const [shown, setShown] = useState(false);
  useEffect(() => {
    if (!ref.current) return;
    const obs = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          setShown(true);
          obs.disconnect();
        }
      },
      { threshold, rootMargin: "0px 0px -40px 0px" }
    );
    obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, shown];
}

const Reveal = ({ children, delay = 0, as: As = "div", className = "", ...rest }) => {
  const [ref, shown] = useReveal();
  return (
    <As
      ref={ref}
      className={className}
      style={{
        opacity: shown ? 1 : 0,
        transform: shown ? "translateY(0)" : "translateY(20px)",
        transition: `opacity 0.8s cubic-bezier(.22,.61,.36,1) ${delay}s, transform 0.8s cubic-bezier(.22,.61,.36,1) ${delay}s`,
      }}
      {...rest}
    >
      {children}
    </As>
  );
};

// ─── Atomic UI ─────────────────────────────────────────────────────────────
const Eyebrow = ({ children, light = false, className = "" }) => (
  <p
    className={`text-[11px] md:text-xs uppercase tracking-[0.22em] font-semibold ${className}`}
    style={{ fontFamily: SANS, color: light ? "rgba(247,245,240,0.7)" : C.textSecondary }}
  >
    {children}
  </p>
);

const PrimaryCTA = ({ href, children, testId, className = "" }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    data-testid={testId}
    className={`inline-flex items-center justify-center px-8 py-3 text-[14px] tracking-[0.02em] font-medium transition-all duration-300 ${className}`}
    style={{
      fontFamily: SANS,
      backgroundColor: C.accent,
      color: C.onAccent,
      borderRadius: 0,
    }}
    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = C.accentHover)}
    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = C.accent)}
  >
    {children}
  </a>
);

// ─── Section components ────────────────────────────────────────────────────
function TopNav() {
  return (
    <nav
      className="absolute top-0 left-0 right-0 z-40 px-6 md:px-12 lg:px-24"
      data-testid="top-nav"
    >
      <div className="flex items-center justify-between h-20 md:h-24">
        <a href="https://haevn.co" aria-label="HAEVN">
          {/* Light logo on mobile (dark hero), dark logo on desktop (cream bg) */}
          <img src={LOGO_LIGHT} alt="HAEVN" className="h-7 md:hidden w-auto" />
          <img src={LOGO_DARK} alt="HAEVN" className="hidden md:block h-8 w-auto" />
        </a>
        <a
          href="#process"
          className="hidden md:inline-block text-[11px] uppercase tracking-[0.22em] font-medium"
          style={{ fontFamily: SANS, color: C.textPrimary }}
        >
          How it works
        </a>
      </div>
    </nav>
  );
}

function Hero({ surveyUrl }) {
  return (
    <section
      className="relative w-full"
      data-testid="hero-section-a"
      style={{ backgroundColor: C.bg }}
    >
      <TopNav />

      {/* ─── Desktop: split screen ─── */}
      <div className="hidden md:grid grid-cols-12 min-h-[88vh]">
        {/* Text column */}
        <div className="col-span-5 flex items-center px-6 md:px-12 lg:px-16 pt-32 pb-24">
          <div className="max-w-xl">
            <Reveal>
              <Eyebrow className="mb-8 md:mb-10">Private Matching Network • Austin</Eyebrow>
            </Reveal>

            <Reveal delay={0.08}>
              <h1
                className="text-5xl md:text-6xl lg:text-[5.25rem] leading-[0.98] tracking-[-0.01em] mb-8"
                style={{ fontFamily: SERIF, color: C.textPrimary, fontWeight: 300 }}
                data-testid="hero-headline"
              >
                Dating apps made finding someone feel like a&nbsp;
                <em className="italic" style={{ fontWeight: 400 }}>second job.</em>
              </h1>
            </Reveal>

            <Reveal delay={0.16}>
              <p
                className="text-lg md:text-xl leading-relaxed mb-10 md:mb-12 max-w-md"
                style={{ fontFamily: SANS, color: C.textSecondary, fontWeight: 300 }}
              >
                HAEVN is a private matching network that continuously looks for compatible people on your behalf. Complete your survey once, then let the system do the searching.
              </p>
            </Reveal>

            <Reveal delay={0.24}>
              <PrimaryCTA href={surveyUrl} testId="hero-cta">
                Take the Survey
              </PrimaryCTA>
            </Reveal>

            <Reveal delay={0.32}>
              <div
                className="flex items-center gap-4 mt-12 pt-8"
                style={{ borderTop: `1px solid ${C.hairline}` }}
                data-testid="trust-bar"
              >
                <div className="flex -space-x-2">
                  {TRUST_AVATARS.map((img, i) => (
                    <img
                      key={i}
                      src={img}
                      alt=""
                      className="w-8 h-8 rounded-full object-cover object-top"
                      style={{ border: `2px solid ${C.bg}` }}
                    />
                  ))}
                </div>
                <p
                  className="text-sm leading-snug"
                  style={{ fontFamily: SANS, color: C.textSecondary, fontWeight: 300 }}
                >
                  People in Austin are already joining the network.&nbsp;
                  <span style={{ color: C.accent, fontWeight: 500 }}>Join them.</span>
                </p>
              </div>
            </Reveal>
          </div>
        </div>

        {/* Image column */}
        <div className="col-span-7 relative" data-testid="hero-image-col">
          <div className="relative h-full w-full overflow-hidden">
            <img
              src={HERO_IMAGE}
              alt="A quiet moment in Austin"
              className="w-full h-full object-cover"
              style={{ transform: "scaleX(-1)", objectPosition: "center 35%" }}
            />
            <div
              aria-hidden
              className="absolute inset-0 pointer-events-none"
              style={{
                background:
                  "linear-gradient(90deg, rgba(250,250,248,0.55) 0%, rgba(250,250,248,0) 35%, rgba(0,0,0,0) 100%)",
              }}
            />
          </div>
        </div>
      </div>

      {/* ─── Mobile: full-bleed image with text overlay ─── */}
      <div className="md:hidden relative">
        <div className="relative h-[78vh] min-h-[560px] w-full overflow-hidden">
          <img
            src={HERO_IMAGE}
            alt="A quiet moment in Austin"
            className="absolute inset-0 w-full h-full object-cover"
            style={{ transform: "scaleX(-1)", objectPosition: "65% 40%" }}
          />
          {/* Dual gradient: dark base for legibility */}
          <div
            aria-hidden
            className="absolute inset-0 pointer-events-none"
            style={{
              background:
                "linear-gradient(180deg, rgba(26,26,26,0.50) 0%, rgba(26,26,26,0.30) 40%, rgba(26,26,26,0.85) 100%)",
            }}
          />

          {/* Content stack pinned to bottom */}
          <div className="absolute inset-0 flex flex-col justify-end px-6 pb-8 pt-20">
            <Reveal>
              <p
                className="text-[11px] uppercase tracking-[0.22em] font-semibold mb-5"
                style={{ fontFamily: SANS, color: "rgba(250,250,248,0.7)" }}
              >
                Private Matching Network • Austin
              </p>
            </Reveal>

            <Reveal delay={0.08}>
              <h1
                className="text-[2.4rem] leading-[1.04] tracking-[-0.01em] mb-5"
                style={{ fontFamily: SERIF, color: "#FAFAF8", fontWeight: 300 }}
                data-testid="hero-headline-mobile"
              >
                Dating apps made finding someone feel like a{" "}
                <em className="italic" style={{ fontWeight: 400 }}>second job.</em>
              </h1>
            </Reveal>

            <Reveal delay={0.16}>
              <p
                className="text-[15px] leading-[1.55] mb-7 max-w-sm"
                style={{ fontFamily: SANS, color: "rgba(250,250,248,0.85)", fontWeight: 300 }}
              >
                HAEVN is a private matching network that continuously looks for compatible people on your behalf. Complete your survey once, then let the system do the searching.
              </p>
            </Reveal>

            <Reveal delay={0.24}>
              <a
                href={surveyUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-cta-mobile"
                className="inline-flex items-center justify-center w-full sm:w-auto px-8 py-3 text-[14px] tracking-[0.02em] font-medium transition-colors duration-300"
                style={{
                  fontFamily: SANS,
                  backgroundColor: C.accent,
                  color: C.onAccent,
                  borderRadius: 0,
                }}
              >
                Take the Survey
              </a>
            </Reveal>

            {/* Mobile trust bar — integrated, on dark */}
            <Reveal delay={0.32}>
              <div
                className="flex items-center gap-3 mt-6 pt-5"
                style={{ borderTop: `1px solid rgba(250,250,248,0.18)` }}
                data-testid="trust-bar-mobile"
              >
                <div className="flex -space-x-2">
                  {TRUST_AVATARS.map((img, i) => (
                    <img
                      key={i}
                      src={img}
                      alt=""
                      className="w-7 h-7 rounded-full object-cover object-top"
                      style={{ border: `2px solid #1A1A1A` }}
                    />
                  ))}
                </div>
                <p
                  className="text-xs leading-snug"
                  style={{ fontFamily: SANS, color: "rgba(250,250,248,0.78)", fontWeight: 300 }}
                >
                  People in Austin are already joining.{" "}
                  <span style={{ color: C.accent, fontWeight: 500 }}>Join them.</span>
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}

function WhatIsHaevn() {
  return (
    <section
      className="py-24 md:py-40 px-6 md:px-12 lg:px-24"
      style={{ backgroundColor: C.bg, borderTop: `1px solid ${C.hairline}`, borderBottom: `1px solid ${C.hairline}` }}
      data-testid="what-section"
    >
      <div className="max-w-6xl mx-auto grid md:grid-cols-12 gap-10 md:gap-16">
        <div className="md:col-span-3">
          <Reveal>
            <Eyebrow>01 / What is HAEVN</Eyebrow>
          </Reveal>
        </div>
        <div className="md:col-span-9 max-w-3xl">
          <Reveal>
            <h2
              className="text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-[-0.01em] mb-12 md:mb-16"
              style={{ fontFamily: SERIF, color: C.textPrimary, fontWeight: 300 }}
            >
              HAEVN is not another dating app. It&apos;s a matching network that{" "}
              <em className="italic" style={{ fontWeight: 400 }}>keeps working,</em> so you don&apos;t have to.
            </h2>
          </Reveal>

          <div className="space-y-6 md:space-y-7">
            <Reveal delay={0.08}>
              <p className="text-lg md:text-xl leading-[1.7]" style={{ fontFamily: SANS, color: C.textSecondary, fontWeight: 300 }}>
                You define what you&apos;re looking for once. HAEVN continuously looks for people who match your criteria in the background. When a match reaches 80%+ mutual compatibility, both of you are notified at the same time.
              </p>
            </Reveal>
            <Reveal delay={0.16}>
              <p
                className="text-xl md:text-2xl leading-snug pt-2"
                style={{ fontFamily: SERIF, color: C.textPrimary, fontWeight: 400, fontStyle: "italic" }}
              >
                No swiping. No chaos. No stress.
              </p>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}

function HowItWorks() {
  const steps = [
    {
      n: "Step 01",
      title: "Define what you want.",
      body: "Take a 10 to 15 minute survey, once. Get specific about what you&apos;re actually looking for. Short-term, long-term, casual, nontraditional. There is no wrong answer.",
    },
    {
      n: "Step 02",
      title: "We do the looking.",
      body: "HAEVN continuously evaluates the network for people who meet your criteria. You only ever see matches that are 80% compatible or higher.",
    },
    {
      n: "Step 03",
      title: "Connect when it makes sense.",
      body: "Review your matches and choose who to reach out to. No swiping. No endless searching. No part-time job.",
    },
  ];

  return (
    <section
      id="process"
      className="py-24 md:py-40 px-6 md:px-12 lg:px-24"
      style={{ backgroundColor: C.surface }}
      data-testid="how-section"
    >
      <div className="max-w-6xl mx-auto">
        <div className="grid md:grid-cols-12 gap-10 md:gap-16 mb-16 md:mb-24">
          <div className="md:col-span-3">
            <Reveal>
              <Eyebrow>02 / Process</Eyebrow>
            </Reveal>
          </div>
          <div className="md:col-span-9">
            <Reveal>
              <h2
                className="text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-[-0.01em]"
                style={{ fontFamily: SERIF, color: C.textPrimary, fontWeight: 300 }}
              >
                How it works.
              </h2>
            </Reveal>
          </div>
        </div>

        <div className="grid md:grid-cols-3" style={{ borderTop: `1px solid ${C.hairline}` }}>
          {steps.map((s, i) => (
            <Reveal
              key={i}
              delay={i * 0.12}
              className="px-0 md:px-8 py-10 md:py-14"
              style={{
                borderRight: i < steps.length - 1 ? `1px solid ${C.hairline}` : "none",
                borderBottom: `1px solid ${C.hairline}`,
              }}
            >
              <p
                className="text-[11px] uppercase tracking-[0.22em] font-semibold mb-8"
                style={{ fontFamily: SANS, color: C.accent }}
              >
                {s.n}
              </p>
              <h3
                className="text-2xl md:text-3xl leading-tight mb-5"
                style={{ fontFamily: SERIF, color: C.textPrimary, fontWeight: 400 }}
                data-testid={`step-${i + 1}-title`}
              >
                {s.title}
              </h3>
              <p
                className="text-base leading-relaxed"
                style={{ fontFamily: SANS, color: C.textSecondary, fontWeight: 300 }}
                dangerouslySetInnerHTML={{ __html: s.body }}
              />
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.2}>
          <p
            className="mt-16 md:mt-20 text-2xl md:text-3xl lg:text-4xl leading-tight max-w-3xl"
            style={{ fontFamily: SERIF, color: C.textPrimary, fontWeight: 300 }}
          >
            You don&apos;t scroll. You don&apos;t search.{" "}
            <em className="italic" style={{ color: C.textSecondary, fontWeight: 400 }}>
              You define what you want, and we do the rest.
            </em>
          </p>
        </Reveal>
      </div>
    </section>
  );
}

function Membership() {
  return (
    <section
      className="py-24 md:py-40 px-6 md:px-12 lg:px-24"
      style={{ backgroundColor: C.surface, borderTop: `1px solid ${C.hairline}`, borderBottom: `1px solid ${C.hairline}` }}
      data-testid="membership-section"
    >
      <div className="max-w-6xl mx-auto grid md:grid-cols-12 gap-10 md:gap-16">
        <div className="md:col-span-3">
          <Reveal>
            <Eyebrow>03 / Membership</Eyebrow>
          </Reveal>
        </div>
        <div className="md:col-span-9 max-w-3xl">
          <Reveal>
            <h2
              className="text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-[-0.01em] mb-12 md:mb-16"
              style={{ fontFamily: SERIF, color: C.textPrimary, fontWeight: 300 }}
            >
              Start free. <em className="italic" style={{ fontWeight: 400 }}>Upgrade when HAEVN proves itself.</em>
            </h2>
          </Reveal>

          <div className="space-y-6 md:space-y-7">
            <Reveal delay={0.08}>
              <p className="text-lg md:text-xl leading-[1.7]" style={{ fontFamily: SANS, color: C.textSecondary, fontWeight: 300 }}>
                If connection is something you&apos;re looking for, it makes sense to be in the network.
              </p>
            </Reveal>
            <Reveal delay={0.16}>
              <p className="text-lg md:text-xl leading-[1.7]" style={{ fontFamily: SANS, color: C.textSecondary, fontWeight: 300 }}>
                Become a <span style={{ color: C.textPrimary, fontWeight: 500 }}>HAEVN+</span> member only after HAEVN starts delivering compatible matches.
              </p>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}



function FinalCTA({ surveyUrl }) {
  return (
    <section
      className="py-32 md:py-48 px-6 md:px-12 lg:px-24"
      style={{ backgroundColor: C.surface, borderTop: `1px solid ${C.hairline}` }}
      data-testid="final-cta-section-a"
    >
      <div className="max-w-3xl mx-auto text-center">
        <Reveal>
          <Eyebrow className="mb-10">Take the next step</Eyebrow>
        </Reveal>
        <Reveal delay={0.08}>
          <h2
            className="text-5xl md:text-7xl leading-[1.02] tracking-[-0.01em] mb-12"
            style={{ fontFamily: SERIF, color: C.textPrimary, fontWeight: 300 }}
          >
            Define what you want.{" "}
            <em className="italic block mt-2" style={{ fontWeight: 400 }}>
              We&apos;ll find who fits.
            </em>
          </h2>
        </Reveal>
        <Reveal delay={0.16}>
          <PrimaryCTA href={surveyUrl} testId="final-cta-btn">
            Take the Survey
          </PrimaryCTA>
        </Reveal>
        <Reveal delay={0.24}>
          <p
            className="mt-8 text-sm"
            style={{ fontFamily: SANS, color: C.textSecondary, fontWeight: 300 }}
          >
            Free to join. Only pay if you want to connect.
          </p>
        </Reveal>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer
      className="px-6 md:px-12 lg:px-24 py-14 md:py-16"
      style={{ backgroundColor: C.darkBg }}
      data-testid="footer-a"
      data-dark-section="true"
    >
      <div className="max-w-6xl mx-auto">
        <div
          className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 pb-10"
          style={{ borderBottom: `1px solid rgba(247,245,240,0.12)` }}
        >
          <div>
            <img src={LOGO_LIGHT} alt="HAEVN" className="h-7 md:h-8 mb-5" />
            <p
              className="text-sm max-w-xs leading-relaxed"
              style={{ fontFamily: SANS, color: "rgba(247,245,240,0.6)", fontWeight: 300 }}
            >
              Private by design. We only notify you when there&apos;s a match worth your time. Never spam.
            </p>
          </div>
          <div
            className="flex gap-8 text-[11px] uppercase tracking-[0.22em]"
            style={{ fontFamily: SANS, color: "rgba(247,245,240,0.6)", fontWeight: 500 }}
          >
            <a href="#" className="hover:opacity-100 transition-opacity" style={{ opacity: 0.8 }}>Privacy</a>
            <a href="#" className="hover:opacity-100 transition-opacity" style={{ opacity: 0.8 }}>Terms</a>
          </div>
        </div>
        <p
          className="pt-8 text-xs"
          style={{ fontFamily: SANS, color: "rgba(247,245,240,0.35)", fontWeight: 300 }}
        >
          &copy; {new Date().getFullYear()} HAEVN. Austin, TX.
        </p>
      </div>
    </footer>
  );
}

function MobileStickyCTA({ surveyUrl }) {
  return (
    <div
      className="md:hidden fixed bottom-0 left-0 right-0 z-50 px-4 py-3"
      style={{
        backgroundColor: "rgba(247,245,240,0.92)",
        backdropFilter: "blur(12px)",
        WebkitBackdropFilter: "blur(12px)",
        borderTop: `1px solid ${C.hairline}`,
      }}
      data-testid="mobile-sticky-cta"
    >
      <a
        href={surveyUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="block w-full text-center py-3 text-[14px] tracking-[0.02em] font-medium transition-colors duration-300"
        style={{ fontFamily: SANS, backgroundColor: C.accent, color: C.onAccent, borderRadius: 0 }}
        data-testid="mobile-sticky-cta-link"
      >
        Take the Survey
      </a>
    </div>
  );
}

// ─── Page ──────────────────────────────────────────────────────────────────
export default function PageA() {
  const surveyUrl = getSurveyUrl();

  return (
    <div
      className="min-h-screen"
      style={{ backgroundColor: C.bg, color: C.textPrimary }}
      data-testid="page-a"
    >
      <Hero surveyUrl={surveyUrl} />
      <WhatIsHaevn />
      <HowItWorks />
      <Membership />
      <FinalCTA surveyUrl={surveyUrl} />
      <Footer />
      <MobileStickyCTA surveyUrl={surveyUrl} />
      {/* Bottom spacer to avoid sticky CTA overlap on mobile */}
      <div className="md:hidden h-20" aria-hidden />
    </div>
  );
}
